"use client";

import { Icon } from "@/components/ui/icon";
import { SocialNetworkIcon } from "@/components/ui/social-network-icon";
import { ensureAbsoluteUrl } from "@/lib/url/ensure-absolute-url";
import { toSocialNetwork, type SocialNetwork } from "../../lib/socials";
import { EmptyCell } from "./empty";
import type { CandidateCellRow } from "./row";

interface SocialLink {
  key: string;
  url: string;
  network: SocialNetwork | null;
}

function socialLinks(row: CandidateCellRow): SocialLink[] {
  const links: SocialLink[] = [];
  const seen = new Set<string>();
  const push = (key: string, url: string, network: SocialNetwork | null) => {
    seen.add(key);
    links.push({ key, url: ensureAbsoluteUrl(url), network });
  };

  if (row.linkedinUrl) push("linkedin", row.linkedinUrl, "linkedin.com");
  if (row.githubUrl) push("github", row.githubUrl, "github.com");
  if (row.twitterUrl) push("twitter", row.twitterUrl, "twitter.com");
  for (const sp of row.socialProfiles) {
    const net = sp.network?.toLowerCase();
    if (!net || seen.has(net) || !sp.url) continue;
    push(net, sp.url, toSocialNetwork(net));
  }
  return links;
}

export function SocialsCell({ row }: { row: CandidateCellRow }) {
  const links = socialLinks(row);
  if (links.length === 0) return <EmptyCell />;

  return (
    <div className="flex items-center gap-2.5">
      {links.map((link) => (
        <a
          key={link.key}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="hover:opacity-50 transition-opacity"
        >
          {link.network ? (
            <SocialNetworkIcon network={link.network} className="size-3.5" />
          ) : (
            <Icon name="IconGlobe" size={14} className="text-foreground opacity-60" />
          )}
        </a>
      ))}
    </div>
  );
}
